import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, User, Mail, Lock, Save, Loader2, CreditCard, Calendar, RefreshCw, XCircle, MessageCircle, ExternalLink } from "lucide-react";

export default function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  
  // Profile data
  const [profile, setProfile] = useState({
    email: "",
    company_name: ""
  });
  
  // Subscription data
  const [subscription, setSubscription] = useState({
    is_active: false,
    subscription_end: null,
    days_remaining: 0,
    can_cancel: false,
    subscription_details: null
  });
  
  // Password change data
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  useEffect(() => {
    loadProfile();
    loadSubscription();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await apiClient.get("/auth/profile");
      setProfile({
        email: response.data.email || "",
        company_name: response.data.company_name || ""
      });
    } catch (error) {
      console.error("Failed to load profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async () => {
    try {
      const response = await apiClient.get("/subscriptions/status");
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to load subscription:", error);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    if (!profile.email) {
      toast.error("Email is required");
      return;
    }
    
    setSaving(true);
    try {
      await apiClient.put("/auth/profile", {
        email: profile.email,
        company_name: profile.company_name
      });
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Failed to update profile:", error);
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error("All password fields are required");
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await apiClient.put("/auth/change-password", {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success("Password changed successfully");
      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: ""
      });
    } catch (error) {
      console.error("Failed to change password:", error);
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm("Are you sure you want to cancel your subscription? You will still have access until the end of your current billing period.")) {
      return;
    }
    
    setCancellingSubscription(true);
    try {
      const response = await apiClient.post("/subscriptions/cancel");
      toast.success(response.data.message);
      loadSubscription(); // Refresh subscription status
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivating(true);
    try {
      const response = await apiClient.post("/subscriptions/reactivate");
      toast.success(response.data.message);
      loadSubscription(); // Refresh subscription status
    } catch (error) {
      console.error("Failed to reactivate subscription:", error);
      toast.error(error.response?.data?.detail || "Failed to reactivate subscription");
    } finally {
      setReactivating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/dashboard")}
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center gap-3 mb-8">
          <User className="w-8 h-8 text-blue-900" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Account Settings</h1>
            <p className="text-sm text-slate-600">Manage your profile, subscription, and security settings</p>
          </div>
        </div>

        {/* Subscription Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Subscription
            </CardTitle>
            <CardDescription>Manage your subscription and billing</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-700">Status</p>
                  <p className={`text-lg font-semibold ${subscription.is_active ? 'text-green-600' : 'text-red-600'}`}>
                    {subscription.is_active ? 'Active' : 'Inactive'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">Renews On</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {formatDate(subscription.subscription_end)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">Days Remaining</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {subscription.days_remaining} days
                  </p>
                </div>
              </div>
              
              {subscription.subscription_details?.cancel_at_period_end && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Cancellation Scheduled:</strong> Your subscription will not renew after {formatDate(subscription.subscription_details.current_period_end)}. 
                    You can reactivate anytime before then.
                  </p>
                </div>
              )}
              
              <div className="flex gap-3">
                {subscription.can_cancel && !subscription.subscription_details?.cancel_at_period_end && (
                  <Button 
                    variant="outline" 
                    onClick={handleCancelSubscription}
                    disabled={cancellingSubscription}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="cancel-subscription-btn"
                  >
                    {cancellingSubscription ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Cancelling...
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel Auto-Renewal
                      </>
                    )}
                  </Button>
                )}
                
                {subscription.subscription_details?.cancel_at_period_end && (
                  <Button 
                    variant="outline" 
                    onClick={handleReactivateSubscription}
                    disabled={reactivating}
                    className="text-green-600 border-green-200 hover:bg-green-50"
                    data-testid="reactivate-subscription-btn"
                  >
                    {reactivating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Reactivating...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reactivate Subscription
                      </>
                    )}
                  </Button>
                )}
              </div>
              
              <p className="text-xs text-slate-500">
                Your subscription automatically renews unless cancelled. Cancel anytime - you&apos;ll keep access until the end of your billing period.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Profile Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Profile Information
            </CardTitle>
            <CardDescription>Update your email and company details</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  placeholder="your@email.com"
                  data-testid="email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  type="text"
                  value={profile.company_name}
                  onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                  placeholder="Your Company"
                  data-testid="company-name-input"
                />
              </div>
              
              <Button 
                type="submit" 
                disabled={saving}
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Password Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Change Password
            </CardTitle>
            <CardDescription>Update your password for security</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label htmlFor="current_password">Current Password</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  placeholder="Enter current password"
                  data-testid="current-password-input"
                />
              </div>
              
              <div>
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  placeholder="Enter new password (min 6 characters)"
                  data-testid="new-password-input"
                />
              </div>
              
              <div>
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>
              
              <Button 
                type="submit" 
                disabled={changingPassword}
                variant="outline"
                data-testid="change-password-btn"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Contact Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Contact SmartReqs
            </CardTitle>
            <CardDescription>Have questions or feedback? We&apos;d love to hear from you!</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Whether you have questions about features, need help with your account, or want to share feedback to help us improve SmartReqs, our team is here to assist you.
              </p>
              
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <Mail className="w-6 h-6 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-slate-700">Email Us</p>
                  <a 
                    href="mailto:admin@smartreqs.ai?subject=SmartReqs Support Request"
                    className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                    data-testid="contact-email-link"
                  >
                    admin@smartreqs.ai
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              
              <div className="text-xs text-slate-500">
                <p><strong>Response time:</strong> We typically respond within 24-48 hours during business days.</p>
              </div>
              
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => window.location.href = 'mailto:admin@smartreqs.ai?subject=SmartReqs Support Request'}
                data-testid="send-email-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Us an Email
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
